import {Box} from '@mui/material';
import {t} from 'i18next';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ArrSectionCard from '../../../../common/arr-accordion-v2/ArrSectionCard';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import AutoGrowTextField from '../auto-grow-text-field';
import {
  IssueCodeDefinition,
  IssueCoding,
  TopLevelIssueCategory,
  updateProtocolSection,
} from '../../../../../redux/slice/aiReviewSliceV2';
import {RootState} from '../../../../../redux/store';
import IssueCodesTabView from './IssueCodesTabView';
import StringListField from './StringListField';
import {
  appendArrayItem,
  buildAddItemAriaLabel,
  createEmptyCategory,
  createEmptyIssueCode,
  createEmptyStringListItem,
  matchesIssueCodeSearch,
  removeArrayItem,
  updateArrayItem,
} from './utils';
import BackToTopButton from '../back-to-top/BackToTopButton';
import './IssueCodesTab.scss';

export type IssueCodesTabMode = 'edit' | 'view';

export interface IssueCodesTabProps {
  mode?: IssueCodesTabMode;
  data?: IssueCoding;
}

type IndexedIssueCode = {
  code: IssueCodeDefinition;
  index: number;
};

const ISSUE_CODES_TOOLTIP_KEY_PREFIX =
  'ai.review.protocolWizard.issueCodes.tooltips';

const IssueCodesTabEdit = () => {
  const dispatch = useDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const issueCoding = useSelector(
    (state: RootState) => state.aiReviewV2.protocol.issueCoding
  );
  const [searchTerm, setSearchTerm] = useState('');

  const {
    top_level_issue_categories: categories,
    issue_codes: issueCodes,
    cross_issue_overlap_rules: overlapRules,
    edge_case_handling: edgeCaseHandling,
  } = issueCoding;

  const updateIssueCoding = (data: Partial<typeof issueCoding>) => {
    dispatch(updateProtocolSection({section: 'issueCoding', data}));
  };

  const filteredIssueCodes: IndexedIssueCode[] = useMemo(
    () =>
      issueCodes
        .map((code, index) => ({code, index}))
        .filter(({code}) => matchesIssueCodeSearch(code, searchTerm)),
    [issueCodes, searchTerm]
  );

  useEffect(() => {
    if (categories.length === 0) {
      dispatch(
        updateProtocolSection({
          section: 'issueCoding',
          data: {top_level_issue_categories: [createEmptyCategory()]},
        })
      );
    }
  }, [categories.length, dispatch]);

  useEffect(() => {
    if (issueCodes.length === 0) {
      dispatch(
        updateProtocolSection({
          section: 'issueCoding',
          data: {issue_codes: [createEmptyIssueCode()]},
        })
      );
    }
  }, [issueCodes.length, dispatch]);

  useEffect(() => {
    const data: Partial<typeof issueCoding> = {};
    if (overlapRules.length === 0) {
      data.cross_issue_overlap_rules = [createEmptyStringListItem()];
    }
    if (edgeCaseHandling.length === 0) {
      data.edge_case_handling = [createEmptyStringListItem()];
    }
    if (Object.keys(data).length > 0) {
      updateIssueCoding(data);
    }
  }, [edgeCaseHandling.length, overlapRules.length]);

  const updateCategory = (
    index: number,
    field: keyof TopLevelIssueCategory,
    value: string
  ) => {
    updateIssueCoding({
      top_level_issue_categories: updateArrayItem(categories, index, {
        ...categories[index],
        [field]: value,
      }),
    });
  };

  const updateIssueCode = (
    index: number,
    field: keyof IssueCodeDefinition,
    value: IssueCodeDefinition[keyof IssueCodeDefinition]
  ) => {
    updateIssueCoding({
      issue_codes: updateArrayItem(issueCodes, index, {
        ...issueCodes[index],
        [field]: value,
      }),
    });
  };

  const updateIssueCodeStringList = (
    index: number,
    field:
      | 'include_if_any_apply'
      | 'exclude_if_any_apply'
      | 'responsive_examples'
      | 'non_responsive_examples',
    listIndex: number,
    value: string
  ) => {
    const currentList = issueCodes[index][field];
    updateIssueCode(
      index,
      field,
      updateArrayItem(currentList, listIndex, value)
    );
  };

  const addIssueCodeStringListItem = (
    index: number,
    field:
      | 'include_if_any_apply'
      | 'exclude_if_any_apply'
      | 'responsive_examples'
      | 'non_responsive_examples'
  ) => {
    const currentList = issueCodes[index][field];
    updateIssueCode(
      index,
      field,
      appendArrayItem(currentList, createEmptyStringListItem())
    );
  };

  const deleteIssueCodeStringListItem = (
    index: number,
    field:
      | 'include_if_any_apply'
      | 'exclude_if_any_apply'
      | 'responsive_examples'
      | 'non_responsive_examples',
    listIndex: number
  ) => {
    const currentList = issueCodes[index][field];
    const nextList =
      currentList.length > 1
        ? removeArrayItem(currentList, listIndex)
        : [createEmptyStringListItem()];
    updateIssueCode(index, field, nextList);
  };

  const updateSimpleStringList = (
    field: 'cross_issue_overlap_rules' | 'edge_case_handling',
    index: number,
    value: string
  ) => {
    const currentList = issueCoding[field];
    updateIssueCoding({
      [field]: updateArrayItem(currentList, index, value),
    });
  };

  const addSimpleStringListItem = (
    field: 'cross_issue_overlap_rules' | 'edge_case_handling'
  ) => {
    const currentList = issueCoding[field];
    updateIssueCoding({
      [field]: appendArrayItem(currentList, createEmptyStringListItem()),
    });
  };

  const deleteSimpleStringListItem = (
    field: 'cross_issue_overlap_rules' | 'edge_case_handling',
    index: number
  ) => {
    const currentList = issueCoding[field];
    const nextList =
      currentList.length > 1
        ? removeArrayItem(currentList, index)
        : [createEmptyStringListItem()];
    updateIssueCoding({[field]: nextList});
  };

  const renderCategoryItem = (
    category: TopLevelIssueCategory,
    index: number
  ) => (
    <>
      <ArrSectionCard.Row className="issue-codes-tab__category-row">
        <ArrSectionCard.Item width={160} flex={0} minWidth={160} maxWidth={160}>
          <ArrTextField
            sizeVariant="medium"
            fullWidth
            className="issue-codes-tab__field"
            placeholder={t(
              'ai.review.protocolWizard.issueCodes.fields.categoryId'
            )}
            value={category.category_id}
            onChange={(event) =>
              updateCategory(index, 'category_id', event.target.value)
            }
            dataTestId={`issue-codes-category-id-${index}`}
          />
        </ArrSectionCard.Item>
        <ArrSectionCard.Item flex={1} minWidth={0}>
          <ArrTextField
            sizeVariant="medium"
            fullWidth
            className="issue-codes-tab__field"
            placeholder={t(
              'ai.review.protocolWizard.issueCodes.fields.categoryLabel'
            )}
            value={category.category_label}
            onChange={(event) =>
              updateCategory(index, 'category_label', event.target.value)
            }
            dataTestId={`issue-codes-category-label-${index}`}
          />
        </ArrSectionCard.Item>
      </ArrSectionCard.Row>
      <AutoGrowTextField
        className="issue-codes-tab__field"
        placeholder={t(
          'ai.review.protocolWizard.issueCodes.fields.categoryDescription'
        )}
        value={category.category_description}
        ariaLabel={t(
          'ai.review.protocolWizard.issueCodes.fields.categoryDescription'
        )}
        onChange={(value) =>
          updateCategory(index, 'category_description', value)
        }
        dataTestId={`issue-codes-category-description-${index}`}
      />
    </>
  );

  const renderIssueCodeItem = ({code, index}: IndexedIssueCode) => (
    <>
      <ArrSectionCard.Row className="issue-codes-tab__issue-code-row">
        <ArrSectionCard.Item width={320} flex={0} minWidth={320} maxWidth={320}>
          <ArrTextField
            sizeVariant="medium"
            fullWidth
            className="issue-codes-tab__field"
            placeholder={t('ai.review.protocolWizard.issueCodes.fields.codeId')}
            value={code.code_id}
            onChange={(event) =>
              updateIssueCode(index, 'code_id', event.target.value)
            }
            dataTestId={`issue-codes-code-id-${index}`}
          />
        </ArrSectionCard.Item>
        <ArrSectionCard.Item flex={1} minWidth={0}>
          <ArrTextField
            sizeVariant="medium"
            fullWidth
            className="issue-codes-tab__field"
            placeholder={t(
              'ai.review.protocolWizard.issueCodes.fields.codeName'
            )}
            value={code.code_name}
            onChange={(event) =>
              updateIssueCode(index, 'code_name', event.target.value)
            }
            dataTestId={`issue-codes-code-name-${index}`}
          />
        </ArrSectionCard.Item>
      </ArrSectionCard.Row>
      <ArrSectionCard.Row className="issue-codes-tab__issue-code-meta-row">
        <ArrSectionCard.Item flex={1} minWidth={0}>
          <ArrTextField
            sizeVariant="medium"
            fullWidth
            className="issue-codes-tab__field"
            placeholder={t(
              'ai.review.protocolWizard.issueCodes.fields.category'
            )}
            value={code.category}
            onChange={(event) =>
              updateIssueCode(index, 'category', event.target.value)
            }
            dataTestId={`issue-codes-code-category-${index}`}
          />
        </ArrSectionCard.Item>
        <ArrSectionCard.Item flex={1} minWidth={0}>
          <ArrTextField
            sizeVariant="medium"
            fullWidth
            className="issue-codes-tab__field"
            placeholder={t(
              'ai.review.protocolWizard.issueCodes.fields.priority'
            )}
            value={code.priority}
            onChange={(event) =>
              updateIssueCode(index, 'priority', event.target.value)
            }
            dataTestId={`issue-codes-code-priority-${index}`}
          />
        </ArrSectionCard.Item>
      </ArrSectionCard.Row>
      <AutoGrowTextField
        className="issue-codes-tab__field"
        placeholder={t('ai.review.protocolWizard.issueCodes.fields.definition')}
        value={code.definition}
        ariaLabel={t('ai.review.protocolWizard.issueCodes.fields.definition')}
        onChange={(value) => updateIssueCode(index, 'definition', value)}
        dataTestId={`issue-codes-code-definition-${index}`}
      />
      <Box className="issue-codes-tab__nested-lists">
        <Box className="issue-codes-tab__nested-lists-row">
          <Box className="issue-codes-tab__nested-lists-item">
            <StringListField
              label={t(
                'ai.review.protocolWizard.issueCodes.fields.includeIfAnyApply'
              )}
              items={code.include_if_any_apply}
              placeholder={t(
                'ai.review.protocolWizard.issueCodes.fields.addItem'
              )}
              dataTestId={`issue-codes-include-${index}`}
              onChange={(listIndex, value) =>
                updateIssueCodeStringList(
                  index,
                  'include_if_any_apply',
                  listIndex,
                  value
                )
              }
              onAdd={() =>
                addIssueCodeStringListItem(index, 'include_if_any_apply')
              }
              onDelete={(listIndex) =>
                deleteIssueCodeStringListItem(
                  index,
                  'include_if_any_apply',
                  listIndex
                )
              }
            />
          </Box>
          <Box className="issue-codes-tab__nested-lists-item">
            <StringListField
              label={t(
                'ai.review.protocolWizard.issueCodes.fields.responsiveExamples'
              )}
              items={code.responsive_examples}
              placeholder={t(
                'ai.review.protocolWizard.issueCodes.fields.addItem'
              )}
              dataTestId={`issue-codes-responsive-${index}`}
              onChange={(listIndex, value) =>
                updateIssueCodeStringList(
                  index,
                  'responsive_examples',
                  listIndex,
                  value
                )
              }
              onAdd={() =>
                addIssueCodeStringListItem(index, 'responsive_examples')
              }
              onDelete={(listIndex) =>
                deleteIssueCodeStringListItem(
                  index,
                  'responsive_examples',
                  listIndex
                )
              }
            />
          </Box>
        </Box>
        <Box className="issue-codes-tab__nested-lists-row">
          <Box className="issue-codes-tab__nested-lists-item">
            <StringListField
              label={t(
                'ai.review.protocolWizard.issueCodes.fields.excludeIfAnyApply'
              )}
              items={code.exclude_if_any_apply}
              placeholder={t(
                'ai.review.protocolWizard.issueCodes.fields.addItem'
              )}
              dataTestId={`issue-codes-exclude-${index}`}
              onChange={(listIndex, value) =>
                updateIssueCodeStringList(
                  index,
                  'exclude_if_any_apply',
                  listIndex,
                  value
                )
              }
              onAdd={() =>
                addIssueCodeStringListItem(index, 'exclude_if_any_apply')
              }
              onDelete={(listIndex) =>
                deleteIssueCodeStringListItem(
                  index,
                  'exclude_if_any_apply',
                  listIndex
                )
              }
            />
          </Box>

          <Box className="issue-codes-tab__nested-lists-item">
            <StringListField
              label={t(
                'ai.review.protocolWizard.issueCodes.fields.nonResponsiveExamples'
              )}
              items={code.non_responsive_examples}
              placeholder={t(
                'ai.review.protocolWizard.issueCodes.fields.addItem'
              )}
              dataTestId={`issue-codes-non-responsive-${index}`}
              onChange={(listIndex, value) =>
                updateIssueCodeStringList(
                  index,
                  'non_responsive_examples',
                  listIndex,
                  value
                )
              }
              onAdd={() =>
                addIssueCodeStringListItem(index, 'non_responsive_examples')
              }
              onDelete={(listIndex) =>
                deleteIssueCodeStringListItem(
                  index,
                  'non_responsive_examples',
                  listIndex
                )
              }
            />
          </Box>
        </Box>
      </Box>
    </>
  );

  const addItemPlaceholder = t(
    'ai.review.protocolWizard.issueCodes.fields.addItem'
  );

  const renderSimpleStringItem = (value: string, index: number) => (
    <AutoGrowTextField
      className="issue-codes-tab__field"
      placeholder={addItemPlaceholder}
      value={value}
      ariaLabel={buildAddItemAriaLabel(
        t(
          'ai.review.protocolWizard.issueCodes.sections.crossIssueOverlapRules'
        ),
        addItemPlaceholder
      )}
      onChange={(nextValue) =>
        updateSimpleStringList('cross_issue_overlap_rules', index, nextValue)
      }
      dataTestId={`issue-codes-overlap-rule-${index}`}
    />
  );

  const renderEdgeCaseItem = (value: string, index: number) => (
    <AutoGrowTextField
      className="issue-codes-tab__field"
      placeholder={addItemPlaceholder}
      value={value}
      ariaLabel={buildAddItemAriaLabel(
        t('ai.review.protocolWizard.issueCodes.sections.edgeCaseHandling'),
        addItemPlaceholder
      )}
      onChange={(nextValue) =>
        updateSimpleStringList('edge_case_handling', index, nextValue)
      }
      dataTestId={`issue-codes-edge-case-${index}`}
    />
  );

  return (
    <Box
      ref={scrollContainerRef}
      className="issue-codes-tab"
      data-testid="issue-codes-tab"
      role="region"
      aria-label={t('ai.review.newJobDetail.issueCodes')}
    >
      <ArrSectionCard
        accordion
        label={t(
          'ai.review.protocolWizard.issueCodes.sections.topLevelCategories'
        )}
        showInfoIcon
        infoContent={t(`${ISSUE_CODES_TOOLTIP_KEY_PREFIX}.topLevelCategories`)}
        items={categories}
        renderItem={renderCategoryItem}
        showAddButton
        showDeleteButton={categories.length > 1}
        addButtonLabel={t('common.add')}
        onAdd={() =>
          updateIssueCoding({
            top_level_issue_categories: appendArrayItem(
              categories,
              createEmptyCategory()
            ),
          })
        }
        onDelete={(index) => {
          if (categories.length <= 1) return;
          updateIssueCoding({
            top_level_issue_categories: removeArrayItem(categories, index),
          });
        }}
        dataTestId="issue-codes-top-level-categories"
      />

      <ArrSectionCard
        accordion
        label={
          <>
            {t('ai.review.protocolWizard.issueCodes.sections.issueCodes')}
            <span
              className="issue-codes-tab__required-marker"
              aria-label="required"
            >
              *
            </span>
          </>
        }
        showInfoIcon
        infoContent={t(`${ISSUE_CODES_TOOLTIP_KEY_PREFIX}.issueCodes`)}
        items={filteredIssueCodes}
        renderItem={renderIssueCodeItem}
        showAddButton
        showDeleteButton={issueCodes.length > 1}
        addButtonLabel={t('common.add')}
        beforeBody={
          <>
            <Box className="issue-codes-tab__search">
              <ArrTextField
                type="search"
                sizeVariant="medium"
                placeholder={t(
                  'ai.review.protocolWizard.issueCodes.fields.searchIssueCodes'
                )}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                dataTestId="issue-codes-search"
                aria-label={t(
                  'ai.review.protocolWizard.issueCodes.fields.searchIssueCodes'
                )}
              />
            </Box>
            {searchTerm.trim() && filteredIssueCodes.length === 0 && (
              <Box
                className="issue-codes-tab__no-results typo-text body3_size"
                role="status"
                aria-live="polite"
              >
                {t(
                  'ai.review.protocolWizard.issueCodes.fields.noIssueCodesFound'
                )}
              </Box>
            )}
          </>
        }
        onAdd={() =>
          updateIssueCoding({
            issue_codes: appendArrayItem(issueCodes, createEmptyIssueCode()),
          })
        }
        onDelete={(displayIndex) => {
          if (issueCodes.length <= 1) return;
          const originalIndex = filteredIssueCodes[displayIndex]?.index;
          if (originalIndex === undefined) return;
          updateIssueCoding({
            issue_codes: removeArrayItem(issueCodes, originalIndex),
          });
        }}
        dataTestId="issue-codes-issue-codes"
        className="issue-codes-tab__issue-codes-section"
      />

      <ArrSectionCard
        accordion
        label={t(
          'ai.review.protocolWizard.issueCodes.sections.crossIssueOverlapRules'
        )}
        showInfoIcon
        infoContent={t(
          `${ISSUE_CODES_TOOLTIP_KEY_PREFIX}.crossIssueOverlapRules`
        )}
        items={overlapRules}
        renderItem={renderSimpleStringItem}
        showAddButton
        showDeleteButton={overlapRules.length > 1}
        addButtonLabel={t('common.add')}
        onAdd={() => addSimpleStringListItem('cross_issue_overlap_rules')}
        onDelete={(index) =>
          overlapRules.length > 1 &&
          deleteSimpleStringListItem('cross_issue_overlap_rules', index)
        }
        dataTestId="issue-codes-overlap-rules"
        className="issue-codes-tab__plain-section"
      />

      <ArrSectionCard
        accordion
        label={t(
          'ai.review.protocolWizard.issueCodes.sections.edgeCaseHandling'
        )}
        showInfoIcon
        infoContent={t(`${ISSUE_CODES_TOOLTIP_KEY_PREFIX}.edgeCaseHandling`)}
        items={edgeCaseHandling}
        renderItem={renderEdgeCaseItem}
        showAddButton
        showDeleteButton={edgeCaseHandling.length > 1}
        addButtonLabel={t('common.add')}
        onAdd={() => addSimpleStringListItem('edge_case_handling')}
        onDelete={(index) =>
          edgeCaseHandling.length > 1 &&
          deleteSimpleStringListItem('edge_case_handling', index)
        }
        dataTestId="issue-codes-edge-case-handling"
        className="issue-codes-tab__plain-section"
      />
      <BackToTopButton scrollContainerRef={scrollContainerRef} />
    </Box>
  );
};

const IssueCodesTab: React.FC<IssueCodesTabProps> = ({
  mode = 'edit',
  data,
}) => {
  if (mode === 'view') {
    return <IssueCodesTabView data={data} />;
  }

  return <IssueCodesTabEdit />;
};

export default IssueCodesTab;
